import { useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Phone } from "lucide-react";
import { Logo } from "./Logo";
import { NAV_LINKS, SITE } from "../lib/data";

const Header = () => {
  const [open, setOpen] = useState(false);
  const location = useLocation();

  return (
    <header
      data-testid="site-header"
      className="sticky top-0 z-50 border-b border-black/10 bg-white/75 backdrop-blur-xl"
    >
      <div className="mx-auto flex h-20 max-w-7xl items-center justify-between px-5 lg:px-8">
        <Link to="/" data-testid="header-logo-link" aria-label="Petroline Services home" onClick={() => setOpen(false)}>
          <Logo />
        </Link>

        <nav className="hidden items-center gap-7 lg:flex" aria-label="Primary">
          {NAV_LINKS.map((link) => (
            <NavLink
              key={link.href}
              to={link.href}
              data-testid={`nav-${link.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `text-sm font-medium tracking-wide transition-colors duration-200 hover:text-ember ${
                  isActive ? "text-ember" : "text-neutral-600"
                }`
              }
            >
              {link.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden items-center gap-5 lg:flex">
          <a
            href={SITE.phoneHref}
            data-testid="header-phone-link"
            className="flex items-center gap-2 text-sm font-bold tracking-wide text-neutral-800 transition-colors hover:text-ember"
          >
            <Phone className="h-4 w-4 text-ember" />
            {SITE.phone}
          </a>
          <Link
            to="/contact"
            data-testid="header-request-service-btn"
            className="bg-ember px-5 py-2.5 font-display text-sm font-bold uppercase tracking-wider text-coal transition-transform duration-200 hover:-translate-y-0.5"
          >
            Request Service
          </Link>
        </div>

        <div className="flex items-center gap-3 lg:hidden">
          <a
            href={SITE.phoneHref}
            data-testid="mobile-call-btn"
            aria-label={`Call ${SITE.phone}`}
            className="flex items-center gap-2 bg-ember px-4 py-2.5 font-display text-xs font-bold uppercase tracking-wider text-coal"
          >
            <Phone className="h-4 w-4" />
            Call
          </a>
          <button
            data-testid="mobile-menu-toggle"
            aria-label={open ? "Close menu" : "Open menu"}
            onClick={() => setOpen(!open)}
            className="border border-black/15 p-2.5 text-neutral-800"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            key={location.pathname}
            data-testid="mobile-menu"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
            className="overflow-hidden border-t border-black/10 bg-white lg:hidden"
            aria-label="Mobile"
          >
            <div className="space-y-1 px-5 py-6">
              {NAV_LINKS.map((link, i) => (
                <motion.div
                  key={link.href}
                  initial={{ opacity: 0, x: -16 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.05 * i }}
                >
                  <NavLink
                    to={link.href}
                    data-testid={`mobile-nav-${link.label.toLowerCase().replace(/\s+/g, "-")}`}
                    onClick={() => setOpen(false)}
                    className={({ isActive }) =>
                      `block py-2.5 font-display text-2xl font-bold tracking-tight ${
                        isActive ? "text-ember" : "text-neutral-700"
                      }`
                    }
                  >
                    {link.label}
                  </NavLink>
                </motion.div>
              ))}
              <Link
                to="/contact"
                data-testid="mobile-request-service-btn"
                onClick={() => setOpen(false)}
                className="mt-4 block bg-ember px-5 py-3.5 text-center font-display text-sm font-bold uppercase tracking-wider text-coal"
              >
                Request Service
              </Link>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Header;
